import { Search, Gauge, MapPin, Compass, Wrench, Target, Megaphone, Sparkles } from "lucide-react";

export const ICONS = { Search, Gauge, MapPin, Compass, Wrench, Target, Megaphone, Sparkles };

const PROCESS = [
  {
    t: "Discover",
    d: "We audit your site, your competitors and the searches that should be yours.",
  },
  {
    t: "Plan",
    d: "A prioritized roadmap in plain language — no jargon, no 40-page PDFs.",
  },
  {
    t: "Execute",
    d: "We implement, measure and refine — with Todd reviewing everything personally.",
  },
];

export const SERVICES = [
  {
    slug: "website-optimization",
    route: "/website-optimization",
    num: "01",
    icon: "Gauge",
    title: "Website Design & Optimization",
    tagline: "A faster site that converts",
    img: "/img/todd-ramp-home.webp",
    heroArt: "/img/web-design-illustration.webp",
    heroArtAlt: "Website design and optimization illustration",
    copy: "The pages that leak, fixed — and new pages built to convert. Design, speed, structure and measurement that make the traffic you already have start paying for itself.",
    page: {
      title: "Website Design & Optimization — ZD Design",
      metaDesc:
        "Website design and optimization that fixes speed, structure and conversion. Turn the traffic you already have into leads. ZD Design, led by Todd Meisler.",
      lead: "The pages that leak, fixed — and new pages built to convert. Design, speed, structure and measurement that make the traffic you already have start paying for itself.",
      includes: [
        "Website design and builds — clean, credible pages that put your number where the eye lands",
        "Speed work — because slow pages lose customers before they load",
        "Page structure and copy that guides visitors to act",
        "Landing pages built for one job: converting",
        "Mobile experience fixes where most of your traffic lives",
        "Measurement setup so every change is judged honestly",
      ],
      fits: [
        "Sites getting traffic but not calls",
        "Pages that load slowly on phones",
        "Businesses about to spend on ads — fix the leak first",
      ],
      fitsLead:
        "Before you buy more traffic, make your website worth visiting:",
    },
  },
  {
    slug: "seo",
    route: "/seo",
    num: "02",
    icon: "Search",
    title: "SEO & Google Visibility",
    tagline: "Own the searches that matter",
    heroArt: "/img/seo-team-illustration.webp",
    heroArtAlt: "SEO and Google visibility team illustration",
    copy: "Own the searches that matter. We clear technical debt, build topical authority, and rank you where buying decisions happen — not just where vanity keywords live.",
    page: {
      title: "SEO & Google Visibility Services — ZD Design",
      metaDesc:
        "SEO for home-service companies and small business: technical fixes, topical authority and local rankings where buying decisions happen. Led by Todd Meisler.",
      lead: "Own the searches that matter. We clear technical debt, build topical authority, and rank you where buying decisions happen — not just where vanity keywords live.",
      includes: [
        "Keyword & market research — the terms your customers actually type",
        "Technical & on-page SEO — speed, structure, and crawlable pages",
        "Off-page optimization and citation building",
        "Local SEO — map-pack visibility and location pages that rank",
        "Google Business Profile optimization, posts, and review strategy",
        "Content strategy built for topical authority, not word count",
        "Competitive research — the gaps your rivals haven't noticed",
        "Conversion recommendations so rankings turn into calls",
        "Plain-language reporting — you always know what moved and why",
      ],
      spotlight: {
        title: "SEO for HVAC, electrical, plumbing & home-service companies",
        lead: "Home services is where we do our best work. Emergency searches, map packs, and \"near me\" queries are where your customers live — and where we've spent the most years winning.",
        points: [
          { t: "HVAC", d: "Seasonal demand, emergency terms, and replacement keywords — tuned to your service area." },
          { t: "Electrical", d: "Panel upgrades, EV chargers, rewires — the searches homeowners type before they call." },
          { t: "Plumbing", d: "Burst pipes don't wait. We put you in front of urgent local searches 24/7." },
          { t: "Home services", d: "Roofing, restoration, and beyond — one presence that compounds across every trade you offer." },
        ],
      },
      fits: [
        "Home-service companies fighting for local searches",
        "HVAC, electrical, plumbing, and roofing companies",
        "Small businesses invisible beyond their own brand name",
        "Sites that rank for their name and nothing else",
      ],
      fitsLead:
        "SEO isn't a magic trick — it's a system. It works hardest for businesses like these:",
    },
  },
  {
    slug: "google-ads",
    route: "/ad-remarketing",
    num: "03",
    icon: "Target",
    title: "Google Ads Management",
    tagline: "Search intent, turned into calls",
    heroArt: "/img/google-ads-illustration.webp",
    heroArtAlt: "Creative meets media illustration",
    copy: "Your business in front of people actively searching for what you sell — campaigns built, bid and refined by the person who runs them, with remarketing that wins back the visitors who left without buying.",
    page: {
      title: "Google Ads Management — ZD Design",
      metaDesc:
        "Google Ads management: search campaigns, remarketing and conversion tracking — built and refined by Todd Meisler. Pay-per-click that turns into calls.",
      lead: "Your business in front of people actively searching for what you sell — campaigns built, bid and refined by the person who runs them, with remarketing that wins back the visitors who left without buying.",
      includes: [
        "Search campaigns built around the queries that end in a call",
        "Remarketing — win back the visitors who left without buying",
        "Conversion tracking that shows what actually works",
        "Budget optimization — spend follows results, not habits",
        "Creative refresh so ads don't wear out",
        "Transparent reporting — spend, results, next moves",
      ],
      fits: [
        "Businesses ready to pay for high-intent traffic",
        "Sites that convert but need more visitors",
        "Anyone burning budget on ads nobody sees",
      ],
      fitsLead:
        "Google Ads put you in front of people mid-search. We make the clicks worth paying for:",
    },
  },
  {
    slug: "google-business-profile",
    route: "/google-business-profile",
    num: "04",
    icon: "MapPin",
    title: "Google Business Profile & Local Search",
    tagline: "Win the local pack",
    heroArt: "/img/gbp-illustration.webp",
    heroArtAlt: "Google Business Profile listing illustration",
    copy: "The local pack is the new yellow pages. We optimize your profile and local presence to pull calls, reviews, and map rankings in your market.",
    page: {
      title: "Google Business Profile & Local Search — ZD Design",
      metaDesc:
        "Google Business Profile optimization for local pack rankings, calls and reviews. Get found in your market. ZD Design, led by Todd Meisler.",
      lead: "The local pack is the new yellow pages. We optimize your profile and local presence to pull calls, reviews, and map rankings in your market.",
      includes: [
        "Full profile optimization — every field working for you",
        "Review strategy that builds steady, honest ratings",
        "Posts, photos and updates that keep the profile alive",
        "Local pack targeting for the searches that end in calls",
        "Local search strategy across maps and \"near me\" queries",
        "Citation cleanup so Google trusts your details",
      ],
      fits: [
        "Home-service companies living off local searches",
        "Storefronts competing for the map pack",
        "Businesses with great reviews nobody sees",
      ],
      fitsLead:
        "For local businesses, your profile is the front door. We make it a good one:",
    },
  },
  {
    slug: "facebook-instagram-ads",
    route: "/facebook-ads",
    num: "05",
    icon: "Megaphone",
    title: "Facebook & Instagram Ads",
    tagline: "Reach the audiences ready to buy",
    heroArt: "/img/facebook-instagram-illustration.webp",
    heroArtAlt: "Facebook and Instagram ads illustration",
    copy: "Precisely targeted campaigns on Facebook and Instagram — brand awareness, solid leads, and remarketing that keeps your business in front of people who already showed interest.",
    page: {
      title: "Facebook & Instagram Ads — ZD Design",
      metaDesc:
        "Facebook and Instagram advertising: targeted campaigns, creative and remarketing that build awareness and capture leads. ZD Design, led by Todd Meisler.",
      lead: "Precisely targeted campaigns on Facebook and Instagram — brand awareness, solid leads, and remarketing that keeps your business in front of people who already showed interest.",
      includes: [
        "Campaigns targeted to the exact audiences you choose",
        "Creative and copy built to stop the scroll",
        "Remarketing to warm audiences until they convert",
        "One objective per campaign — awareness, leads or traffic, measured",
        "Budget optimization across placements",
      ],
      fits: [
        "Businesses whose customers live on social",
        "Local brands building awareness in their market",
        "Advertisers who want reach beyond search",
      ],
      fitsLead:
        "Facebook and Instagram let you aim at exactly who you want. We keep it honest and measured:",
    },
  },
  {
    slug: "consulting-strategy",
    route: "/coaching-consulting",
    num: "06",
    icon: "Compass",
    title: "Marketing Strategy & Consulting",
    tagline: "The work that connects the levers",
    heroArt: "/img/consulting-illustration.webp",
    heroArtAlt: "Marketing strategy and consulting illustration",
    copy: "The work that connects the levers. One-on-one strategy with Todd — one plan across your website, SEO and ads, so every channel pulls the same direction.",
    page: {
      title: "Marketing Strategy & Consulting with Todd — ZD Design",
      metaDesc:
        "One-on-one marketing strategy and consulting with Todd Meisler: audits, roadmaps and one integrated plan across website, SEO and ads.",
      lead: "The work that connects the levers. One-on-one strategy with Todd — one plan across your website, SEO and ads, so every channel pulls the same direction.",
      includes: [
        "One-on-one sessions with Todd — never a junior account manager",
        "One integrated plan across website, SEO and ads",
        "A full audit of your site, search presence and competitors",
        "Funnel mapping — from first search to signed customer",
        "A prioritized roadmap you could execute without us (but won't want to)",
        "Honest answers — including when something isn't worth the money",
      ],
      fits: [
        "Owners tired of agencies that speak in riddles",
        "Companies running SEO and ads that don't talk to each other",
        "Anyone who wants to understand their own numbers",
      ],
      fitsLead:
        "All clients work one-on-one with Todd. Strategy & consulting is for when you want the whole picture:",
    },
  },
  {
    slug: "cross-web-repair",
    num: "07",
    icon: "Wrench",
    title: "Cross-Web Repair Program",
    tagline: "Fix the gaps everywhere",
    heroArt: "/img/cross-web-repair-illustration.webp",
    heroArtAlt: "Cross-web repair illustration",
    route: "/cross-web-repair",
  },
  {
    slug: "ai-search-visibility",
    num: "08",
    icon: "Sparkles",
    title: "AI Search Visibility",
    tagline: "Get named in AI answers",
    heroArt: "/img/todd-ramp-full.webp",
    heroArtAlt: "Todd Meisler, founder and chief strategist at ZD Design",
    heroArtClass: "top-[62%] -translate-y-1/2",
    route: "/ai-search-visibility",
    copy: "Your customers are asking ChatGPT, Gemini and Claude who the best option is — not just Google. We combine our SEO program with AI-search visibility into one plan that gets you ranked on Google and named in AI answers.",
    page: {
      title: "AI Search Visibility Services (GEO) — ZD Design",
      metaDesc:
        "AI-search visibility plus full Google SEO: crawler access, published unique content and authority building so ChatGPT, Gemini and Claude name your business. Month-to-month, led by Todd Meisler.",
      lead: "Your customers are asking ChatGPT, Gemini and Claude who the best option is — not just Google. We combine our SEO program with AI-search visibility into one plan that gets you ranked on Google and named in AI answers.",
      includes: [
        "Full Google SEO — site fixes, content, backlinks, Google Business Profile",
        "AI-crawler access — llms.txt, robots rules and server-side rendering for bots",
        "Published unique content — the citable pages AI engines quote",
        "Authority building — placement that makes AI engines trust the answer",
        "AI-answer tracking — we monitor when and how you're named",
        "Live dashboard — rankings and AI mentions, updated monthly",
        "Content strategy tuned to what buyers ask AI engines",
        "Competitive research — the answers your rivals already own",
        "Plain-language reporting — you always know what moved and why",
      ],
      fits: [
        "Businesses invisible in ChatGPT, Gemini and Claude answers",
        "Companies who rank on Google but want the AI layer too",
        "Home-service owners who want one plan for both engines",
        "Anyone tired of guessing whether AI marketing is real",
      ],
      fitsLead:
        "AI visibility isn't a magic trick — it sits on top of solid SEO. It works hardest for businesses like these:",
      pricing: {
        rate: "$597",
        label: "Full rate — month-to-month, cancel anytime",
        includes: [
          "Both engines: full Google SEO plus AI-search visibility",
          "Crawlers, published unique content and authority building placement",
          "So ChatGPT, Gemini and Claude name you",
          "Everything shows up in your monthly dashboard",
        ],
        note: "Founding clients lock $397/mo while spots last.",
        link: "/ai-visibility",
        linkText: "See the founding-client offer",
        img: "/img/todd-brick-full.webp",
        imgAlt: "Todd Meisler, founder and strategist at ZD Design",
      },
    },
  },
];

export const PROCESS_STEPS = PROCESS;
